import React from 'react';
import {
  useClickableCard,
  StackLayout,
  Icon,
  GDSStyleObject,
  UtilityText,
  HeadingText,
  ParagraphText,
  StackItem,
} from '@leagueplatform/genesis-core';
import { ConditionalLinkCardRenderer } from '#components/common/conditional-link-card-renderer/conditional-link-card-renderer.component.tsx';
import { ConditionalLinkRenderer } from '#components/common/conditional-link-renderer/conditional-link-renderer.component.tsx';
import { MasonryDeeplinkAction } from '#types/masonry-driver-node-actions.ts';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { MasonryNotificationCardRendererProps } from './masonry-notification-card-content-renderer.component';

const eyebrowStyles: GDSStyleObject = {
  borderRadius: '$medium',
  backgroundColor: '$surfaceBackgroundTertiary',
  padding: '$quarter $half',
  marginBottom: '$half',
};

export const MasonryNotificationCardRenderer = ({
  heading,
  body,
  info,
  eyebrow,
  onClick,
  onDismiss,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
}: MasonryNotificationCardRendererProps) => {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);
  const invokeAction = useInvokeAction();

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean((onClick as MasonryDeeplinkAction)?.payload.url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={onDismiss}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing="$threeQuarters"
        css={{
          padding: '$one',
        }}
      >
        <StackLayout css={{ marginTop: '$quarter' }} verticalAlignment="center">
          {eyebrow && (
            <UtilityText
              css={eyebrowStyles}
              size="eyebrow"
              emphasis="emphasized"
            >
              {eyebrow}
            </UtilityText>
          )}

          <HeadingText level="2" size="sm">
            <ConditionalLinkRenderer
              ref={primaryActionRef}
              disableDefaultOnClick
              onClick={(event: React.MouseEvent) => {
                if (onClick) {
                  invokeAction({
                    event,
                    ...onClick,
                  });
                }
              }}
              url={(onClick as MasonryDeeplinkAction)?.payload.url}
            >
              {heading}
            </ConditionalLinkRenderer>
          </HeadingText>

          {body && (
            <ParagraphText
              css={{ '&:not(:last-child)': { marginBlockEnd: 0 } }}
              htmlStringOptions={BodyTextHtmlTagOptions}
            >
              {body}
            </ParagraphText>
          )}

          {info && (
            <ParagraphText
              emphasis="subtle"
              size="sm"
              htmlStringOptions={BodyTextHtmlTagOptions}
            >
              {info}
            </ParagraphText>
          )}
        </StackLayout>
        {onClick && (
          <StackItem verticalAlignment="center">
            <Icon icon="interfaceChevronForward" />
          </StackItem>
        )}
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
};
